import React, { useState, useEffect } from "react";
import {
    BrowserRouter as Router,
    Route,
    Routes,
    useLocation
} from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "./firebase";
import { ref, set, update, get } from "firebase/database";
import Navbar from "./components/Navbar";
import AdminNavbar from "./components/AdminNavbar";
import Home from "./pages/Home";
import Profile from "./pages/Profile";
import Footer from "./components/Footer";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminDashboard from "./components/AdminDashboard";
import Checkout from "./components/Checkout";
import TambahSaldo from "./components/TambahSaldo";
import DepositStatus from "./components/DepositStatus";
import ProductList from "./components/ProductList";
import OrderStatus from "./components/OrderStatus";
import Panduan from "./components/Panduan";
import Inbox from "./pages/Inbox";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const App = () => {
    const [user, setUser] = useState(null); // Data pengguna
    const [isLoggedIn, setIsLoggedIn] = useState(false); // Status login
    const [saldo, setSaldo] = useState(0); // Saldo pengguna
    const [uid, setUid] = useState(""); // UID pengguna
    const [cart, setCart] = useState([]); // Keranjang belanja

    // Cek autentikasi pengguna
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                const userId = currentUser.uid;

                try {
                    // Ambil data pengguna dari database
                    const userRef = ref(db, "users/" + userId);
                    const snapshot = await get(userRef);

                    if (snapshot.exists()) {
                        const userData = snapshot.val();
                        setSaldo(userData.saldo || 0); // Set saldo dari database
                        setUid(userId); // Set UID
                    } else {
                        // Jika pengguna baru, tambahkan data default ke database
                        await set(userRef, {
                            email: currentUser.email,
                            saldo: 0, // Default saldo
                            isLoggedIn: true,
                            uid: userId
                        });
                        setSaldo(0); // Set saldo default
                    }

                    setUser(currentUser); // Set data pengguna
                    setIsLoggedIn(true); // Set status login
                } catch (error) {
                    console.error("Error mengambil data pengguna:", error);
                }
            } else {
                // Reset data jika pengguna logout
                setUser(null);
                setIsLoggedIn(false);
                setSaldo(0);
                setUid("");
            }
        });

        return () => unsubscribe();
    }, []); // Efek dijalankan sekali saat komponen dimuat

    // Fungsi Logout
    const handleLogout = async () => {
        try {
            if (user) {
                // Update status login menjadi false
                await update(ref(db, "users/" + user.uid), {
                    isLoggedIn: false
                });
            }

            await signOut(auth); // Logout dari Firebase Auth
            setIsLoggedIn(false); // Reset status login
            setUser(null); // Reset pengguna
            setSaldo(0); // Reset saldo
            setUid(""); // Reset UID
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Fungsi untuk menambahkan saldo
    const tambahSaldo = async (jumlah) => {
        if (jumlah <= 0) {
            console.error("Jumlah saldo tidak valid.");
            return;
        }

        try {
            const newSaldo = saldo + jumlah; // Tambah saldo
            await update(ref(db, "users/" + uid), { saldo: newSaldo }); // Update saldo di database
            setSaldo(newSaldo); // Perbarui saldo di state
        } catch (error) {
            console.error("Gagal menambahkan saldo:", error);
        }
    };

    // Fungsi untuk menampilkan navbar dinamis
    const DynamicNavbar = () => {
        const location = useLocation();
        const isAdminPage =
            location.pathname.includes("/admin") ||
            location.pathname.includes("/deposit") ||
            location.pathname.includes("/product");

        return isAdminPage ? (
            <AdminNavbar
                setIsLoggedIn={setIsLoggedIn}
                handleLogout={handleLogout}
            />
        ) : (
            <Navbar setIsLoggedIn={setIsLoggedIn} handleLogout={handleLogout} />
        );
    };

    return (
        <Router>
            <ToastContainer />
            <DynamicNavbar />
            <Routes>
                <Route
                    path="/"
                    element={
                        <Home
                            isLoggedIn={isLoggedIn}
                            setCart={setCart}
                            cart={cart}
                        />
                    }
                />
                <Route path="/profile" element={<Profile saldo={saldo} />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route
                    path="/deposit"
                    element={<TambahSaldo tambahSaldo={tambahSaldo} />}
                />
                <Route path="/status" element={<DepositStatus />} />
                <Route path="/product" element={<ProductList />} />
                <Route path="/order" element={<OrderStatus />} />
                <Route path="/panduan" element={<Panduan />} />
                <Route path="/inbox" element={<Inbox />} />
            </Routes>
            <Footer />
        </Router>
    );
};

export default App;
